<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0">
            <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h6 class="mb-0"><i class="fas fa-industry me-1" style="color: #2e389b;"></i> Producción — Órdenes de producción</h6>
              <div class="d-flex gap-2">
                <button
                  type="button"
                  class="btn btn-sm"
                  style="background-color: #6f7a93; border-color: #6f7a93; color: #fff;"
                  :disabled="downloadingPending"
                  @click="downloadPendingItemsGrouped"
                >
                  <i class="fas" :class="downloadingPending ? 'fa-spinner fa-spin' : 'fa-file-pdf'"></i>
                  PDF prod. agrupado
                </button>
              </div>
            </div>

            <!-- Filtros -->
            <div class="row g-2 mb-3">
              <div class="col-md-3">
                <label class="mb-1 text-xs">Buscar</label>
                <input
                  type="text"
                  v-model="filters.search"
                  class="form-control form-control-sm"
                  placeholder="#Producción, #venta, cliente..."
                  @input="onFilterInput"
                />
              </div>
              <div class="col-md-2">
                <label class="mb-1 text-xs">Estado</label>
                <select v-model="filters.status" class="form-control form-control-sm" @change="applyFilters">
                  <option value="">Todos</option>
                  <option value="in_progress">En proceso</option>
                  <option value="incompleto">Incompleto</option>
                  <option value="terminado">Terminado</option>
                </select>
              </div>
              <div class="col-md-2">
                <label class="mb-1 text-xs">Categoría</label>
                <select v-model="filters.category_id" class="form-control form-control-sm" @change="applyFilters">
                  <option value="">Todas</option>
                  <option v-for="cat in categories" :key="cat.id" :value="cat.id">
                    {{ cat.fullname || cat.name }}
                  </option>
                </select>
              </div>
              <div class="col-md-2">
                <label class="mb-1 text-xs">Fecha inicio</label>
                <input
                  type="date"
                  v-model="filters.date_from"
                  class="form-control form-control-sm"
                  @change="applyFilters"
                />
              </div>
              <div class="col-md-2">
                <label class="mb-1 text-xs">Fecha fin</label>
                <input
                  type="date"
                  v-model="filters.date_to"
                  class="form-control form-control-sm"
                  @change="applyFilters"
                />
              </div>
              <div class="col-md-1 d-flex align-items-end">
                <button
                  type="button"
                  class="btn btn-outline-secondary btn-sm mb-0 w-100"
                  title="Limpiar filtros"
                  @click="clearFilters"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>
          </div>

          <div class="card-body px-0 pt-0 pb-2">
            <div v-if="loading" class="text-center py-5">
              <i class="fas fa-spinner fa-spin fa-2x" style="color: #2e389b;"></i>
              <p class="mt-2 text-sm text-secondary">Cargando producciones...</p>
            </div>

            <div v-else class="table-responsive p-0">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7" style="width: 40px">
                      <input
                        type="checkbox"
                        style="width: 16px; height: 16px; cursor: pointer; accent-color: #2dce89;"
                        :checked="allSelected"
                        title="Seleccionar todas las producciones de la página"
                        @change="toggleSelectAll"
                      />
                    </th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">N°</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Categoría</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Ventas</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Pares</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Fecha</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Observaciones</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Estado</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Asignado</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Avance</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Opciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(order, index) in data" :key="order.id" :class="{ 'day-end': isLastOfDay(index) }">
                    <td class="text-center align-middle">
                      <input
                        type="checkbox"
                        style="width: 16px; height: 16px; cursor: pointer; accent-color: #2dce89;"
                        :value="order.id"
                        v-model="selectedIds"
                      />
                    </td>
                    <td class="align-middle ps-2">
                      <span class="text-sm font-weight-bold">#{{ order.id }}</span>
                    </td>
                    <td class="align-middle text-center">
                      <span class="badge badge-sm" style="background-color: #6f7a93;">{{ order.category?.name || 'Sin categoría' }}</span>
                    </td>
                    <td class="align-middle text-center">
                      <div class="d-flex flex-wrap justify-content-center gap-1">
                        <router-link
                          v-for="remission in order.remissions"
                          :key="remission.id"
                          :to="{ name: 'Production Order', query: { id: remission.id } }"
                          class="badge badge-sm text-decoration-none"
                          style="background-color: #2e389b;"
                          :title="`Ver producción de la venta #${remission.id} — ${remission.name || ''}`"
                        >
                          #{{ remission.id }}
                        </router-link>
                        <span v-if="!(order.remissions || []).length" class="text-xs text-secondary">—</span>
                      </div>
                    </td>
                    <td class="text-center align-middle">
                      <span class="badge badge-sm bg-secondary">{{ order.pairs || 0 }} pares</span>
                    </td>
                    <td class="text-center align-middle">
                      <span class="text-xs text-secondary">{{ formatDate(order.created_at) }}</span>
                    </td>
                    <td class="text-center align-middle">
                      <button
                        type="button"
                        class="obs-box"
                        :class="{ 'obs-box--empty': !order.observation }"
                        :title="order.observation ? 'Editar observación' : 'Agregar observación'"
                        @click="editObservation(order)"
                      >
                        <i class="fas fa-pen me-1"></i>
                        {{ order.observation || 'Agregar observación' }}
                      </button>
                    </td>
                    <td class="text-center align-middle">
                      <select
                        :value="order.status || 'in_progress'"
                        class="status-select"
                        :class="orderStatusPillClass(order.status)"
                        :disabled="statusUpdatingId === order.id"
                        title="Cambiar estado de la producción"
                        @change="onOrderStatusChange($event, order)"
                      >
                        <option value="in_progress">En proceso</option>
                        <option value="incompleto">Incompleto</option>
                        <option value="terminado">Terminado</option>
                      </select>
                    </td>
                    <td class="text-center align-middle">
                      <button
                        type="button"
                        class="status-select"
                        :class="order.assigned_user ? 'assigned-pill' : 'assigned-pill--empty'"
                        :disabled="assignedUpdatingId === order.id"
                        title="Cambiar operario asignado"
                        @click="changeAssignedUser(order)"
                      >
                        <i v-if="assignedUpdatingId === order.id" class="fas fa-spinner fa-spin me-1"></i>
                        {{ order.assigned_user?.name || 'Sin asignar' }}
                      </button>
                    </td>
                    <td class="text-center align-middle">
                      <template v-if="(order.progress_total_units || 0) > 0">
                        <div class="mx-auto" style="width: 110px;">
                          <div class="text-xxs text-secondary mb-1">
                            {{ order.progress_completed_units || 0 }} / {{ order.progress_total_units || 0 }} tareas · {{ progressPercent(order) }}%
                          </div>
                          <div class="prod-progress">
                            <div
                              class="prod-progress-bar"
                              :class="{ 'prod-progress-bar--done': progressPercent(order) >= 100 }"
                              :style="{ width: progressPercent(order) + '%' }"
                            ></div>
                          </div>
                        </div>
                      </template>
                      <span v-else class="text-xs text-secondary">—</span>
                    </td>
                    <td class="text-center align-middle">
                      <div class="d-flex justify-content-center gap-1">
                        <router-link
                          :to="{ name: 'Production Order Details', query: { id: order.id } }"
                          class="btn btn-outline-success btn-sm mb-0"
                          title="Ver detalles de la producción"
                        >
                          <i class="fas fa-eye me-1"></i> Ver detalles
                        </router-link>
                        <button
                          type="button"
                          class="btn btn-outline-danger btn-sm mb-0"
                          :disabled="deletingId === order.id"
                          title="Descartar la producción y devolver las ventas para enviarlas de nuevo"
                          @click="deleteOrder(order)"
                        >
                          <i class="fas" :class="deletingId === order.id ? 'fa-spinner fa-spin' : 'fa-trash'"></i>
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>

                  <tr v-if="data.length === 0">
                    <td colspan="11" class="text-center text-xxs">No hay producciones que coincidan con los filtros.</td>
                  </tr>
                </tbody>
              </table>

              <!-- Paginación -->
              <div class="pagination p-3">
                <a
                  v-for="link in links"
                  :key="link.label"
                  class="page-item cursor-pointer"
                  @click="link.url && !link.active && fetchData(link.url)"
                  :class="{ active: link.active, disabled: !link.url }"
                >
                  {{ link.label }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import $ from 'jquery';
import 'select2/dist/css/select2.css';
import 'select2';
import productionOrdersService from '@/services/productionOrders.service';
import categoriesService from '@/services/categories.service';
import userService from '@/services/user.service';

export default {
  name: 'ProductionInProgress',
  data() {
    return {
      data: [],
      links: [],
      currentPage: 1,
      lastPage: 1,
      page: 1,
      loading: true,
      selectedIds: [],
      downloadingPending: false,
      statusUpdatingId: null,
      assignedUpdatingId: null,
      operarios: [],
      deletingId: null,
      categories: [],
      filters: {
        search: '',
        status: '',
        category_id: '',
        date_from: '',
        date_to: '',
      },
    };
  },
  computed: {
    allSelected() {
      return this.data.length > 0 && this.data.every((d) => this.selectedIds.includes(d.id));
    },
  },
  methods: {
    async fetchData(pageUrl = null) {
      this.loading = true;
      if (pageUrl) {
        const parsed = parseInt(new window.URL(pageUrl).searchParams.get('page'));
        this.page = Number.isNaN(parsed) ? 1 : parsed;
      }

      try {
        const params = { per_page: 50 };
        if (this.filters.status) params.status = this.filters.status;
        if (this.filters.category_id) params.category_id = this.filters.category_id;
        if (this.filters.date_from) params.date_from = this.filters.date_from;
        if (this.filters.date_to) params.date_to = this.filters.date_to;
        if (this.filters.search.trim()) params.search = this.filters.search.trim();

        const res = await productionOrdersService.getAll(this.page, params);
        this.data = res.data.data || [];
        this.links = res.data.links || [];
        this.currentPage = res.data.current_page || 1;
        this.lastPage = res.data.last_page || 1;
        this.selectedIds = [];
      } catch (error) {
        console.error('Error al cargar producciones:', error);
        await Swal.fire('Error', 'No se pudo cargar las producciones', 'error');
      } finally {
        this.loading = false;
      }
    },
    /** Búsqueda con debounce para no consultar en cada tecla */
    onFilterInput() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => this.applyFilters(), 300);
    },
    applyFilters() {
      this.page = 1;
      this.fetchData();
    },
    clearFilters() {
      this.filters = { search: '', status: '', category_id: '', date_from: '', date_to: '' };
      this.applyFilters();
    },
    /** Porcentaje de oficios listos sobre el total de la producción */
    progressPercent(order) {
      const total = parseInt(order.progress_total_units) || 0;
      const done = parseInt(order.progress_completed_units) || 0;
      if (total <= 0) return 0;
      return Math.min(100, Number(((done / total) * 100).toFixed(2)));
    },
    async fetchCategories() {
      try {
        const res = await categoriesService.getCategories();
        this.categories = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      } catch (e) {
        console.error('Error al cargar categorías:', e);
        this.categories = [];
      }
    },
    toggleSelectAll(event) {
      if (event.target.checked) {
        this.selectedIds = this.data.map((d) => d.id);
      } else {
        this.selectedIds = [];
      }
    },
    formatDate(value) {
      if (!value) return '—';
      return new Date(value).toLocaleDateString('es-ES');
    },
    async editObservation(order) {
      const result = await Swal.fire({
        title: `Observación — Producción #${order.id}`,
        input: 'textarea',
        inputValue: order.observation || '',
        inputPlaceholder: 'Escribe algún detalle para identificar el pedido...',
        inputAttributes: { maxlength: 2000 },
        showCancelButton: true,
        confirmButtonText: 'Guardar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#2e389b',
        cancelButtonColor: '#6c757d',
        showLoaderOnConfirm: true,
        allowOutsideClick: () => !Swal.isLoading(),
        preConfirm: async (value) => {
          try {
            const { data } = await productionOrdersService.updateObservation(order.id, value);
            return data.production_order;
          } catch (error) {
            Swal.showValidationMessage(error.response?.data?.message || 'No se pudo guardar la observación');
            return false;
          }
        },
      });
      const saved = result.isConfirmed ? result.value : null;
      const target = saved ? this.data.find((o) => o.id === saved.id) : null;
      if (target) target.observation = saved.observation;
    },
    /** True si la siguiente producción de la lista es de otro día (o es la última) */
    isLastOfDay(index) {
      const next = this.data[index + 1];
      if (!next) return true;
      return this.formatDate(this.data[index].created_at) !== this.formatDate(next.created_at);
    },
    orderStatusPillClass(status) {
      if (status === 'incompleto') return 'status-pill--incompleto';
      if (status === 'terminado') return 'status-pill--terminado';
      return 'status-pill--in-progress';
    },
    /** Oficios (asignaciones) pendientes de la producción */
    pendingOficiosCount(order) {
      return parseInt(order.pending_oficios_count) || 0;
    },
    async onOrderStatusChange(event, order) {
      const newStatus = event.target.value;
      const current = order.status || 'in_progress';
      const target = event.target;

      if (newStatus === current) return;

      // No se puede marcar como Terminado si hay oficios pendientes
      if (newStatus === 'terminado') {
        const pending = this.pendingOficiosCount(order);
        if (pending > 0) {
          target.value = current;
          await Swal.fire({
            icon: 'warning',
            title: 'Cambio no permitido',
            text: `No se puede marcar como Terminado: hay ${pending} oficio(s) pendiente(s) por terminar.`,
            confirmButtonColor: '#2e389b',
          });
          return;
        }
        const result = await Swal.fire({
          title: '¿Confirmar cambio?',
          text: `¿Deseas marcar la producción #${order.id} como Terminado?`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#2dce89',
          cancelButtonColor: '#6c757d',
          confirmButtonText: 'Sí, marcar como Terminado',
          cancelButtonText: 'Cancelar',
        });
        if (!result.isConfirmed) {
          target.value = current;
          return;
        }
      }

      this.statusUpdatingId = order.id;
      try {
        await productionOrdersService.updateStatus(order.id, newStatus);
        this.applyOrderStatus(order, newStatus);
      } catch (error) {
        target.value = current;
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'No se pudo actualizar el estado',
        });
      } finally {
        this.statusUpdatingId = null;
      }
    },
    /** Usuarios activos con rol Operario (3), para asignar producciones */
    async fetchOperarios() {
      try {
        const res = await userService.getAll('all');
        const users = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        this.operarios = users.filter((u) => String(u.role) === '3');
      } catch (e) {
        console.error('Error al cargar operarios:', e);
        this.operarios = [];
      }
    },
    /** Modal con select2 para cambiar el operario asignado a la producción */
    async changeAssignedUser(order) {
      if (!this.operarios.length) await this.fetchOperarios();

      const escape = (text) => $('<div>').text(text ?? '').html();
      const currentId = order.assigned_user_id ? String(order.assigned_user_id) : '';
      const options = this.operarios
        .map((u) => `<option value="${u.id}" ${String(u.id) === currentId ? 'selected' : ''}>${escape(u.name)}</option>`)
        .join('');

      const result = await Swal.fire({
        title: `Operario — Producción #${order.id}`,
        html: `
          <div class="text-start">
            <label class="text-xs mb-1">Operario asignado</label>
            <select id="swal-assigned-user" style="width:100%;">
              <option value="">Sin asignar</option>
              ${options}
            </select>
          </div>`,
        showCancelButton: true,
        confirmButtonText: 'Guardar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#2e389b',
        cancelButtonColor: '#6c757d',
        didOpen: (popup) => {
          $('#swal-assigned-user').select2({
            placeholder: 'Seleccionar operario',
            allowClear: true,
            width: '100%',
            dropdownParent: $(popup),
          });
        },
        willClose: () => {
          const $select = $('#swal-assigned-user');
          if ($select.hasClass('select2-hidden-accessible')) $select.select2('destroy');
        },
        preConfirm: () => $('#swal-assigned-user').val() || null,
      });
      if (!result.isConfirmed) return;

      const newId = result.value ? Number(result.value) : null;
      if (String(newId || '') === currentId) return;

      this.assignedUpdatingId = order.id;
      try {
        const { data } = await productionOrdersService.updateAssignedUser(order.id, newId);
        const saved = data.production_order;
        const target = this.data.find((o) => o.id === saved.id);
        if (target) Object.assign(target, { assigned_user_id: saved.assigned_user_id, assigned_user: saved.assigned_user });
      } catch (error) {
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'No se pudo asignar el operario',
        });
      } finally {
        this.assignedUpdatingId = null;
      }
    },
    applyOrderStatus(order, status) {
      order.status = status;
    },
    /** Descarta la producción y devuelve las ventas para poder reenviarlas */
    async deleteOrder(order) {
      const result = await Swal.fire({
        title: 'Eliminar producción',
        html: `¿Descartar la producción <b>#${order.id}</b>?<br><br>` +
          '<span class="text-sm text-secondary">Se eliminan sus oficios asignados, los productos vuelven a estado <b>Pendiente</b>' +
          ' (el stock de los fabricados se descuenta) y las ventas quedan listas para <b>enviarse de nuevo</b> a producción.</span>',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#e91e63',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
      });
      if (!result.isConfirmed) return;

      this.deletingId = order.id;
      try {
        await productionOrdersService.destroy(order.id);
        await Swal.fire({
          icon: 'success',
          title: 'Producción eliminada',
          text: 'Las ventas quedaron listas para enviarse de nuevo a producción.',
          timer: 1600,
          showConfirmButton: false,
        });
        await this.fetchData();
      } catch (error) {
        console.error('Error al eliminar la producción:', error);
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'No se pudo eliminar la producción',
        });
      } finally {
        this.deletingId = null;
      }
    },
    async downloadPendingItemsGrouped() {
      if (this.selectedIds.length === 0) {
        await Swal.fire('Atención', 'Seleccione al menos una producción', 'warning');
        return;
      }
      this.downloadingPending = true;
      try {
        const response = await productionOrdersService.getPendingItemsGrouped(this.selectedIds);
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `produccion_agrupado_${new Date().toISOString().slice(0, 10)}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error al generar PDF de producciones:', error);
        await Swal.fire('Error', 'No se pudo generar el PDF de producciones', 'error');
      } finally {
        this.downloadingPending = false;
      }
    },
  },
  async mounted() {
    this.fetchCategories();
    this.fetchOperarios();
    await this.fetchData();
  },
};
</script>

<style scoped>
/* Caja de observación de la producción (abre modal para editar) */
.obs-box {
  max-width: 180px;
  padding: 4px 8px;
  border: 1px solid #d2d6da;
  border-radius: 6px;
  background: #fff;
  color: #344767;
  font-size: 0.7rem;
  text-align: left;
  white-space: normal;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  cursor: pointer;
}
.obs-box:hover {
  border-color: #2e389b;
}
.obs-box--empty {
  color: #8392ab;
  border-style: dashed;
}

/* Separador entre producciones de días distintos */
tr.day-end > td {
  border-bottom: 2px solid #2e389b !important;
}

.pagination {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.page-item {
  margin: 0 5px;
  padding: 4px 10px;
  cursor: pointer;
  border-radius: 6px;
}

.page-item.active {
  font-weight: bold;
  background: #2dce89;
  color: #fff;
}

.page-item.disabled {
  opacity: 0.5;
  pointer-events: none;
}

/* Select de estado (igual que remisiones) */
.status-select {
  border: none;
  border-radius: 20px;
  padding: 4px 24px 4px 12px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  outline: none;
  transition: all 0.2s ease;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 6px center;
  background-size: 12px;
}

.status-select:hover {
  opacity: 0.85;
}

.status-select.status-pill--in-progress {
  background-color: #fef3c7;
  color: #b45309;
}

.status-select.status-pill--incompleto {
  background-color: #fce4e4;
  color: #991b1b;
}

.status-select.status-pill--terminado {
  background-color: #d4edda;
  color: #155724;
}

/* Operario asignado (mismo estilo que el select de estado, abre modal) */
.status-select.assigned-pill {
  background-color: #e3e7fb;
  color: #2e389b;
}

.status-select.assigned-pill--empty {
  background-color: #f0f2f5;
  color: #8392ab;
}

/* Barra de progreso de oficios */
.prod-progress {
  width: 100%;
  height: 6px;
  background: #e9ecef;
  border-radius: 999px;
  overflow: hidden;
}

.prod-progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #2e389b, #6c8cff);
  border-radius: 999px;
  transition: width 0.4s ease;
}

.prod-progress-bar--done {
  background: linear-gradient(90deg, #82d616, #4caf50);
}
</style>
